/**
 * Version Switcher Component
 *
 * Compact dropdown for quick version switching
 * Displays current version and allows instant switching to any version
 */

import React, { useState } from 'react';
import { History, ChevronDown, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export interface VersionInfo {
  version: number;
  timestamp: string;
  message: string;
  author: string;
  changes: {
    type: 'create' | 'edit' | 'restore';
    summary: string;
    elements?: string[];
  };
}

interface VersionSwitcherProps {
  versions: VersionInfo[];
  currentVersion: number | null;
  onSwitch: (version: number) => void;
  onOpenHistory?: () => void;
  isSwitching: boolean;
  className?: string;
}

export const VersionSwitcher: React.FC<VersionSwitcherProps> = ({
  versions,
  currentVersion,
  onSwitch,
  onOpenHistory,
  isSwitching,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle version switch
  const handleSwitch = (version: number) => {
    if (version !== currentVersion && !isSwitching) {
      onSwitch(version);
      setIsOpen(false);
    }
  };

  // Sort versions in reverse order (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  if (versions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 gap-2 px-3 font-medium transition-all hover:bg-accent',
            'border-border/60 hover:border-border',
            isSwitching && 'opacity-50 cursor-not-allowed',
            className
          )}
          disabled={isSwitching}
        >
          {isSwitching ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">Switching...</span>
            </>
          ) : (
            <>
              <History className="h-3.5 w-3.5" />
              <span className="text-xs">
                Version {currentVersion}
              </span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 p-2">
        <DropdownMenuLabel className="px-2 py-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Version History</span>
            <span className="text-xs font-normal text-muted-foreground">
              {versions.length} saved
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1" />

        {/* Version list - Compact single-line layout */}
        <div className="max-h-80 overflow-y-auto py-1">
          {sortedVersions.map((versionInfo) => {
            const isCurrent = versionInfo.version === currentVersion;

            return (
              <TooltipProvider key={versionInfo.version} delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onClick={() => handleSwitch(versionInfo.version)}
                      className={cn(
                        'flex items-center gap-2 py-1.5 px-2.5 cursor-pointer rounded-md',
                        'transition-colors duration-150',
                        isCurrent
                          ? 'bg-primary/10 hover:bg-primary/15 border border-primary/20'
                          : 'hover:bg-accent border border-transparent',
                        isCurrent && 'cursor-default'
                      )}
                      disabled={isCurrent || isSwitching}
                    >
                      {/* Version badge - compact */}
                      <Badge
                        variant={isCurrent ? "default" : "secondary"}
                        className={cn(
                          'h-5 w-8 text-[10px] font-bold justify-center px-1',
                          isCurrent && 'shadow-sm'
                        )}
                      >
                        v{versionInfo.version}
                      </Badge>

                      {/* Timestamp - inline without icon */}
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {formatTimestamp(versionInfo.timestamp)}
                      </span>

                      {/* Message - truncated */}
                      <span className="text-xs text-foreground truncate flex-1">
                        {versionInfo.message}
                      </span>

                      {/* Active indicator - compact */}
                      {isCurrent && (
                        <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-medium text-primary border-primary/30 shrink-0">
                          <Check className="h-2.5 w-2.5 mr-0.5" />
                          Active
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs">
                    <p className="text-xs font-medium">Version {versionInfo.version}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{versionInfo.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {new Date(versionInfo.timestamp).toLocaleString()}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>

        {/* View full history button */}
        {onOpenHistory && (
          <>
            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuItem
              onClick={() => {
                onOpenHistory();
                setIsOpen(false);
              }}
              className="text-xs font-medium text-center justify-center text-primary hover:bg-primary/10 py-2"
            >
              View Detailed History
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
